/**
 * Collection product card behavior — port of ProductTileLP.tsx:
 * - base image index depends on the view mode: Produkt = media 1 (idx 0),
 *   Zimmer = media 2 (idx 1, only when multiple images exist)
 * - hover shows the arrow controls (only with multiple images)
 * - arrows cycle images; alternates crossfade 350ms over the base
 * - mouse leave resets to the current view's base image
 * The Produkt/Zimmer toggle lives in collection.js and dispatches
 * `dylys:view-change` ({ zimmer: boolean }) on the grid container.
 *
 * Swatches (F53): clicking a colour re-points the ENTIRE card at that variant —
 * image, price, compare-at, badge, "andere Farbe im Sale" hint and href move
 * together, so the card never prices variant A while linking to variant B.
 * Hover-cycling stays inside the shown colourway: alternates carry
 * data-media-group (the colourway EAN, F30); media matching no colourway is
 * shared and always cycles.
 */
(function () {
  'use strict';

  function init(link) {
    if (link.dataset.cardInit) return;
    link.dataset.cardInit = '1';

    var wrap = link.closest('.product-card-wrap') || link.parentNode;
    var baseImg = link.querySelector('.product-card__base');
    var allAlts = Array.prototype.slice.call(link.querySelectorAll('.product-card__alt'));
    var badge = link.querySelector('[data-card-badge]');
    var priceWrap = link.querySelector('[data-card-price-wrap]');
    var priceEl = link.querySelector('[data-card-price]');
    var compareEl = link.querySelector('[data-card-compare]');
    var hintEl = link.querySelector('[data-card-hint]');
    var swatches = wrap ? Array.prototype.slice.call(wrap.querySelectorAll('[data-card-swatch]')) : [];

    var zimmer = false;
    var imgIdx = 0;
    var alts = [];

    // Alternates that belong to the currently shown colourway (plus shared media)
    function syncAlts() {
      var group = link.getAttribute('data-card-group') || '';
      alts = allAlts.filter(function (img) {
        var g = img.getAttribute('data-media-group') || '';
        var included = !group || !g || g === group;
        img.classList.toggle('is-excluded', !included);
        if (!included) img.classList.remove('is-current');
        return included;
      });
    }

    function imageCount() {
      return alts.length + 1; // + base image
    }

    function baseIdx() {
      return zimmer && imageCount() > 1 ? 1 : 0;
    }

    function render() {
      alts.forEach(function (img, i) {
        img.classList.toggle('is-current', i + 1 === imgIdx);
      });
    }

    syncAlts();

    link.addEventListener('mouseenter', function () {
      link.classList.add('is-hovered');
    });

    link.addEventListener('mouseleave', function () {
      link.classList.remove('is-hovered');
      imgIdx = baseIdx();
      render();
    });

    var prevBtn = link.querySelector('[data-card-prev]');
    var nextBtn = link.querySelector('[data-card-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        imgIdx = (imgIdx - 1 + imageCount()) % imageCount();
        render();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        imgIdx = (imgIdx + 1) % imageCount();
        render();
      });
    }

    // ── Swatch → represent that variant everywhere ───────────────────────────
    function selectSwatch(btn) {
      var d = btn.dataset;

      if (d.variantUrl) link.setAttribute('href', d.variantUrl);
      link.setAttribute('data-card-group', d.variantGroup || '');

      if (baseImg && d.variantImage) {
        // srcset would outrank src — drop it so the colourway image wins
        baseImg.removeAttribute('srcset');
        baseImg.removeAttribute('sizes');
        baseImg.setAttribute('src', d.variantImage);
      }

      if (priceEl) priceEl.textContent = d.variantPrice || '';
      if (compareEl) {
        if (d.variantCompare) {
          compareEl.textContent = d.variantCompare;
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }
      if (priceWrap) priceWrap.classList.toggle('product-card__price--sale', !!d.variantCompare);

      if (badge) {
        badge.textContent = d.variantBadge || '';
        badge.classList.toggle('is-empty', !d.variantBadge);
      }

      if (hintEl) {
        hintEl.textContent = d.variantHint || '';
        hintEl.hidden = !d.variantHint;
      }

      swatches.forEach(function (other) {
        var active = other === btn;
        other.classList.toggle('is-active', active);
        other.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      syncAlts();
      imgIdx = baseIdx();
      render();
    }

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        selectSwatch(btn);
      });
    });

    // View mode sync (source: useEffect on zimmerView — reset idx + hover)
    link.setViewMode = function (isZimmer) {
      zimmer = isZimmer;
      imgIdx = baseIdx();
      link.classList.remove('is-hovered');
      render();
    };
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-product-card]').forEach(init);
  }

  initAll(document);

  document.addEventListener('dylys:view-change', function (e) {
    var grid = e.target;
    var zimmer = !!(e.detail && e.detail.zimmer);
    if (!grid.querySelectorAll) return;
    grid.querySelectorAll('[data-product-card]').forEach(function (card) {
      init(card);
      if (card.setViewMode) card.setViewMode(zimmer);
    });
  }, true);

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  window.Dylys = window.Dylys || {};
  window.Dylys.initProductCards = initAll;
})();
